import { useCallback, useReducer } from 'react';

import { useAnalyticsEvents } from '../../../../common/analytics/generated/use-analytics-events';
import { NAV_STATE_LS_KEY } from '../../../../common/constants';
import type { Store, UserMenuData, UserSideNavData } from '../../../../common/types';

import type { LocalStorageState } from './types';

const nullState: LocalStorageState = {
	userSideNavData: { defaultWidth: undefined, defaultCollapsed: undefined },
	userMenuData: [],
};

// Allow the key to be extended (by adding a prefix or suffix)
let LS_KEY = NAV_STATE_LS_KEY;

export function useLocalSideNavUserData({ app }: { app: string }): Store {
	setLocalStorageKey(app);
	const { fireEvent } = useAnalyticsEvents();
	const reducer = reducerWithAnalytics(fireEvent);

	const [sideNavState, setSideNavState] = useReducer(reducer, getInitialState());

	const setUserMenuData = useCallback((userMenuData: UserMenuData) => {
		setSideNavState({ userMenuData });
	}, []);

	const setUserSideNavData = useCallback(
		(userSideNavData: UserSideNavData) => {
			setSideNavState({
				userSideNavData: { ...sideNavState.userSideNavData, ...userSideNavData },
			});
		},
		[sideNavState],
	);

	return {
		...sideNavState,
		setUserMenuData,
		setUserSideNavData,
	};
}

function setLocalStorageKey(suffix: string) {
	LS_KEY = [NAV_STATE_LS_KEY, suffix].join('-');
}

function getInitialState() {
	// Guard against SSR — localStorage is not available on the server.
	if (typeof window === 'undefined') {
		return nullState;
	}

	// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
	const storedState = parseLocalStorageState(localStorage.getItem(LS_KEY));

	if (storedState) {
		return storedState;
	}

	try {
		setLocalStorageState(nullState);
	} catch (error) {
		// If localStorage fails during initialization, just continue with nullState
		// eslint-disable-next-line no-console
		console.warn('Failed to initialize localStorage state:', error);
	}
	return nullState;
}

function reducerWithAnalytics(fireEvent: ReturnType<typeof useAnalyticsEvents>['fireEvent']) {
	return (state: LocalStorageState, payload: Partial<LocalStorageState>): LocalStorageState => {
		try {
			const newState = { ...state, ...payload };
			// Update localStorage with the new state
			setLocalStorageState(newState);
			fireEvent('operational.executeMutation.succeeded', {
				storage: 'local',
			});
			return newState;
		} catch (error) {
			fireEvent('operational.executeMutation.failed', {
				storage: 'local',
				error: error instanceof Error ? error.message : (String(error) ?? 'Unknown error'),
			});
			// Return the previous state if the update failed
			return state;
		}
	};
}

function setLocalStorageState(state: LocalStorageState) {
	// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
	localStorage.setItem(LS_KEY, JSON.stringify(state));
}

function parseLocalStorageState(state: string | null): LocalStorageState | undefined {
	if (!state) {
		return;
	}

	try {
		const parsedState = JSON.parse(state);

		// This check should return true for the nullState
		if (
			parsedState?.userSideNavData &&
			Array.isArray(parsedState.userMenuData) &&
			typeof parsedState.userSideNavData === 'object'
		) {
			return parsedState satisfies LocalStorageState;
		}
	} catch (error) {
		// eslint-disable-next-line no-console
		console.log(error);
		// something went wrong with parsing, return undefined
	}
}
